// @flow

/**
 * Cookie inventory for the preferences modal, grouped by consent category.
 *
 * DERIVED FROM A CODE AUDIT of THIS repository (business.999.md), not copied
 * from 999.md — the two sites load different things. What business.999.md
 * actually sets/loads today:
 *  - SimpalsID auth + language cookies (src/providers/SimpalsBarProvider,
 *    src/providers/LocaleCode) and the consent cookie itself → `necessary`;
 *  - Google Tag Manager `GTM-WGN77XB` → GA4 cookies → `analytics`;
 *  - the Bitrix24 CRM form loader (public/index.html, `b24form`) → the tariff
 *    request / callback forms on the Shop page → `functionality`.
 *
 * There is deliberately NO advertising section: this app loads no ad network.
 * Yandex Metrika was removed (commit "rm ya metrika") and is not declared.
 *
 * ⚠️ TODO(privacy/legal): VALIDATE before production. This is an engineering
 * audit, not a legal one. Third-party names and durations marked
 * "Provider-defined" must be confirmed against each provider's current cookie
 * declaration (they change).
 *
 * GDPR/UX requirement: the `name` column shows USER-FRIENDLY purpose labels, not
 * technical identifiers (no `cc_consent`, `simpalsid.auth`, …). The technical
 * cookie names are kept as code comments on each row so the audit trail stays
 * intact. RO/RU copy follows the wording of the official 999.md translation
 * (999md_cookies_RO_RU.xlsx); buildConfig.js picks the active locale.
 */

/** Localized free text shown in the cookie table (ro/ru — the app's locales). */
export type LocalizedText = {
  ro: string,
  ru: string,
};

/** One cookie-table row. Keys MUST match the header keys built in buildConfig.js. */
export type CookieRow = {
  desc: LocalizedText,
  domain: string,
  expiration: LocalizedText,
  name: LocalizedText,
};

export const COOKIE_TABLE_ROWS: { [category: string]: CookieRow[] } = {
  necessary: [
    {
      // cookie: cc_consent
      name: {
        ro: 'Alegerea ta privind cookie-urile',
        ru: 'Твой выбор по cookie',
      },
      domain: 'business.999.md',
      desc: {
        ro:
          'Păstrează alegerile tale privind consimțământul pentru cookie-uri, ca să știm ce categorii ai permis.',
        ru:
          'Хранит твой выбор по согласию на cookie, чтобы мы знали, какие категории ты разрешил.',
      },
      expiration: { ro: '6 luni', ru: '6 месяцев' },
    },
    {
      // cookies: simpalsid.auth, simpalsid.auth2
      name: { ro: 'Autentificare în cont', ru: 'Вход в аккаунт' },
      domain: 'business.999.md',
      desc: {
        ro:
          'Token-uri de autentificare care te mențin conectat la contul tău 999.md / SimpalsID.',
        ru:
          'Токены аутентификации, которые держат тебя авторизованным в аккаунте 999.md / SimpalsID.',
      },
      expiration: { ro: 'Sesiune de autentificare', ru: 'Сессия входа' },
    },
    {
      // cookie: simpalsid.sessionKey
      name: { ro: 'Sesiune securizată', ru: 'Защищённая сессия' },
      domain: 'business.999.md',
      desc: {
        ro: 'Menține sesiunea ta securizată pe server.',
        ru: 'Поддерживает твою защищённую серверную сессию.',
      },
      expiration: { ro: 'Sesiune', ru: 'Сессия' },
    },
    {
      // cookie: simpalsid.lang
      name: { ro: 'Limba interfeței', ru: 'Язык интерфейса' },
      domain: '.999.md',
      desc: {
        ro: 'Reține limba interfeței tale (RO/RU).',
        ru: 'Запоминает язык твоего интерфейса (RO/RU).',
      },
      expiration: { ro: 'Permanent', ru: 'Постоянный' },
    },
  ],

  functionality: [
    {
      // provider cookies: Bitrix24 CRM web forms (public/index.html — b24form)
      name: {
        ro: 'Formulare de contact și solicitări (Bitrix24)',
        ru: 'Формы обратной связи и заявок (Bitrix24)',
      },
      domain: 'bitrix24.ru',
      // TODO(privacy): confirm Bitrix24 cookie names & durations.
      desc: {
        ro:
          'Asigură funcționarea formularelor de solicitare a tarifelor și de contactare a departamentului comercial. Fără acest consimțământ, formularele nu se vor deschide.',
        ru:
          'Обеспечивает работу форм заявки на тарифы и обращения в коммерческий отдел. Без этого согласия формы не откроются.',
      },
      expiration: {
        ro: 'Stabilit de furnizor',
        ru: 'Определяется поставщиком',
      },
    },
  ],

  analytics: [
    {
      // cookie: _ga
      name: {
        ro: 'Statistici vizitatori (Google Analytics)',
        ru: 'Статистика посетителей (Google Analytics)',
      },
      domain: '.999.md',
      desc: {
        ro: 'Google Analytics: distinge vizitatorii unici.',
        ru: 'Google Analytics: различает уникальных посетителей.',
      },
      expiration: { ro: '2 ani', ru: '2 года' },
    },
    {
      // cookie: _ga_<container-id>
      name: {
        ro: 'Starea sesiunii de analiză (Google Analytics)',
        ru: 'Состояние аналитической сессии (Google Analytics)',
      },
      domain: '.999.md',
      desc: {
        ro: 'Google Analytics 4: păstrează starea sesiunii pentru proprietate.',
        ru: 'Google Analytics 4: сохраняет состояние сессии для ресурса.',
      },
      expiration: { ro: '2 ani', ru: '2 года' },
    },
    {
      // cookie: _gid
      name: {
        ro: 'Statistici zilnice (Google Analytics)',
        ru: 'Суточная статистика (Google Analytics)',
      },
      domain: '.999.md',
      desc: {
        ro:
          'Google Analytics: distinge vizitatorii pe o perioadă de 24 de ore.',
        ru: 'Google Analytics: различает посетителей в течение 24 часов.',
      },
      expiration: { ro: '24 de ore', ru: '24 часа' },
    },
    {
      // cookies: _gat, _gat_*
      name: {
        ro: 'Limitarea cererilor (Google Analytics)',
        ru: 'Ограничение запросов (Google Analytics)',
      },
      domain: '.999.md',
      desc: {
        ro:
          'Setat de Google pentru a limita rata cererilor (limitează volumul de date colectate pe paginile cu trafic mare).',
        ru:
          'Устанавливается Google для ограничения частоты запросов (ограничивает объём данных, собираемых на страницах с высоким трафиком).',
      },
      expiration: { ro: '1 minut', ru: '1 минута' },
    },
  ],
};
